import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { withAdmin } from 'contexts/admin-context';
import { withToast } from 'contexts/toast-context';
import { AdminRoute } from 'components/basics/routes';
import CalendarAdapter from 'adapters/calendar';
import {
  Breadcrumb, BreadcrumbItem, Container, Row, Col, Card, CardBody,
  Button, Form, FormGroup, Label, Input, Badge, CustomInput, Alert,
} from 'reactstrap';

const AdminSettingsCalendarRoute = (props) => {
  const { history, toast } = props;
  const providerId = props.providerId || (props.provider && props.provider.id);

  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [available, setAvailable] = useState({ google: { available: false }, apple_caldav: { available: true } });
  const [apple, setApple] = useState({ appleId: '', appPassword: '' });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const find = (kind) => integrations.find((i) => i.kind === kind);

  const load = async () => {
    try {
      setLoading(true);
      const res = await CalendarAdapter.status(providerId);
      setIntegrations(res.data || []);
      setAvailable(res.providers || available);
    } catch (e) {
      toast.showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Surface the result of the Google OAuth round-trip (?status=connected|error).
    if (typeof window !== 'undefined') {
      const q = new URLSearchParams(window.location.search);
      const status = q.get('status');
      if (status === 'connected') setNotice({ color: 'success', text: 'Calendar connected successfully.' });
      if (status === 'error') setNotice({ color: 'danger', text: 'Could not connect that calendar. Please try again.' });
    }
    if (providerId) load();
    // eslint-disable-next-line
  }, [providerId]);

  const connectGoogle = async () => {
    try {
      setBusy(true);
      const { url } = await CalendarAdapter.googleConnectUrl(providerId);
      window.location.href = url; // leave the SPA for Google's consent screen
    } catch (e) {
      toast.showError(e);
      setBusy(false);
    }
  };

  const connectApple = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      await CalendarAdapter.connectApple(providerId, apple);
      setApple({ appleId: '', appPassword: '' });
      setNotice({ color: 'success', text: 'Apple iCloud calendar connected.' });
      await load();
    } catch (err) {
      toast.showError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (integ, field, value) => {
    try {
      const updated = await CalendarAdapter.setToggles(providerId, integ.id, { [field]: value });
      setIntegrations(integrations.map((i) => (i.id === integ.id ? { ...i, ...updated } : i)));
    } catch (e) {
      toast.showError(e);
    }
  };

  const disconnect = async (integ) => {
    if (!window.confirm('Disconnect this calendar?')) return;
    try {
      await CalendarAdapter.disconnect(providerId, integ.id);
      await load();
    } catch (e) {
      toast.showError(e);
    }
  };

  const Toggles = ({ integ }) => (
    <div className='mt-3'>
      <CustomInput type='switch' id={`pull-${integ.id}`} label='Block bookings when my calendar is busy'
        checked={!!integ.pullEnabled} onChange={(e) => toggle(integ, 'pullEnabled', e.target.checked)} />
      <CustomInput type='switch' id={`push-${integ.id}`} className='mt-2' label='Add confirmed bookings to my calendar'
        checked={!!integ.pushEnabled} onChange={(e) => toggle(integ, 'pushEnabled', e.target.checked)} />
    </div>
  );

  const google = find('google');
  const appleInteg = find('apple_caldav');

  return (
    <AdminRoute title='Blush Admin - Calendar'>
      <Breadcrumb>
        <BreadcrumbItem><Link to={`/admin/${providerId}/settings`}>Settings</Link></BreadcrumbItem>
        <BreadcrumbItem active>Calendar</BreadcrumbItem>
      </Breadcrumb>

      <Container>
        {notice && <Alert color={notice.color} toggle={() => setNotice(null)}>{notice.text}</Alert>}
        <p className='text-muted'>
          Connect your personal calendar so Blush blocks times you're busy and adds confirmed bookings automatically.
        </p>

        <Row>
          {/* Google Calendar */}
          <Col xs={12} className='mb-3'>
            <Card>
              <CardBody>
                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <h5 className='mb-1'>Google Calendar</h5>
                    {google
                      ? <Badge color='success'>Connected{google.accountEmail ? ` · ${google.accountEmail}` : ''}</Badge>
                      : <span className='text-muted'>Not connected</span>}
                  </div>
                  {google
                    ? <Button color='link' className='text-danger' onClick={() => disconnect(google)}>Disconnect</Button>
                    : available.google && available.google.available
                      ? <Button color='primary' disabled={busy} onClick={connectGoogle}>Connect Google</Button>
                      : <Badge color='warning'>Not configured</Badge>}
                </div>
                {google && <Toggles integ={google} />}
                {!(available.google && available.google.available) && !google &&
                  <small className='text-muted d-block mt-2'>
                    The server needs GOOGLE_CALENDAR_CLIENT_ID / _SECRET set to enable Google sign-in.
                  </small>}
              </CardBody>
            </Card>
          </Col>

          {/* Apple iCloud (CalDAV) */}
          <Col xs={12} className='mb-3'>
            <Card>
              <CardBody>
                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <h5 className='mb-1'>Apple iCloud Calendar</h5>
                    {appleInteg
                      ? <Badge color='success'>Connected{appleInteg.accountEmail ? ` · ${appleInteg.accountEmail}` : ''}</Badge>
                      : <span className='text-muted'>Not connected</span>}
                  </div>
                  {appleInteg &&
                    <Button color='link' className='text-danger' onClick={() => disconnect(appleInteg)}>Disconnect</Button>}
                </div>

                {appleInteg
                  ? <Toggles integ={appleInteg} />
                  : (
                    <Form className='mt-3' onSubmit={connectApple}>
                      <FormGroup>
                        <Label>Apple ID (email)</Label>
                        <Input type='email' value={apple.appleId} required
                          onChange={(e) => setApple({ ...apple, appleId: e.target.value })} placeholder='you@icloud.com' />
                      </FormGroup>
                      <FormGroup>
                        <Label>App-specific password</Label>
                        <Input type='password' value={apple.appPassword} required
                          onChange={(e) => setApple({ ...apple, appPassword: e.target.value })} placeholder='xxxx-xxxx-xxxx-xxxx' />
                        <small className='text-muted'>
                          Generate one at appleid.apple.com → Sign-In &amp; Security → App-Specific Passwords (requires 2FA).
                        </small>
                      </FormGroup>
                      <Button color='dark' type='submit' disabled={busy}>Connect Apple Calendar</Button>
                    </Form>
                  )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {loading && <p className='text-muted'>Loading…</p>}
      </Container>
    </AdminRoute>
  );
};

export default withAdmin(withToast(AdminSettingsCalendarRoute));
